import { DateTime } from 'luxon';
import isURL from 'validator/lib/isURL';
import { GiftCardDelivery } from '../types';
import { GiftRecipientInput, GiftShipsTo } from '../types/gql-op-types';
import { validateEmail } from './util/validateEmail';

export const GIFT_MESSAGE_MAX_LENGTH = 160;
export const GIFT_CARD_MESSAGE_MAX_LENGTH = 255;
const GIFT_CARD_DELIVERY_WINDOW_DAYS = 365;
const GIFT_CARD_DELIVERY_HOUR = 11;

export interface GiftOptionsValue {
  shipsTo: GiftShipsTo;
  email: string;
  message: string;
}

/** translation keys, one per field that web would reject */
export interface GiftOptionsErrors {
  email?: string;
  message?: string;
}

// mirrors web's validateGiftRecipient in purchaseResolver.ts so the shopper sees the error before submit
// eslint-disable-next-line no-control-regex -- deliberately matching control characters to reject them
const CONTROL_CHAR_PATTERN = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/;
const HTML_OR_SCRIPT_PATTERN = /<[^>]*>|javascript:/i;
const WRAPPING_PUNCTUATION = /^[("'[{]+|[.,!?;:)"'\]}]+$/g;
// markdown links and an http(s):// or www. inside a word, which the per-word url check misses
const LINK_PATTERN = /\]\(|https?:\/\/|\bwww\./i;

function isSafeMessage(message: string, maxLength = GIFT_MESSAGE_MAX_LENGTH): boolean {
  return (
    message.length <= maxLength &&
    !CONTROL_CHAR_PATTERN.test(message) &&
    !HTML_OR_SCRIPT_PATTERN.test(message) &&
    !LINK_PATTERN.test(message) &&
    !message.split(/\s+/).some((word) => isURL(word.replace(WRAPPING_PUNCTUATION, '')))
  );
}

export function giftOptionsErrors({ shipsTo, email, message }: GiftOptionsValue): GiftOptionsErrors {
  if (shipsTo === GiftShipsTo.PURCHASER) {
    return {};
  }
  return {
    ...(!validateEmail(email.trim()) && { email: 'gift_recipient_email_invalid' }),
    ...(!isSafeMessage(message.trim()) && { message: 'gift_message_invalid' }),
  };
}

/** translation keys, one per gift card field that web would reject */
export interface GiftCardDeliveryErrors {
  emailAddress?: string;
  personalMessage?: string;
  deliverAt?: string;
}

/** mirrors web's validateGiftCardDeliveries; a null deliverAt sends the card at purchase */
export function giftCardDeliveryErrors(
  { emailAddress, personalMessage, deliverAt }: GiftCardDelivery,
  now = DateTime.local(),
): GiftCardDeliveryErrors {
  const deliverAtDate = deliverAt ? DateTime.fromISO(deliverAt) : undefined;
  const deliverAtOutOfRange =
    deliverAtDate &&
    !(
      deliverAtDate.isValid &&
      deliverAtDate > now &&
      deliverAtDate <= now.plus({ days: GIFT_CARD_DELIVERY_WINDOW_DAYS })
    );
  return {
    ...(!validateEmail(emailAddress) && { emailAddress: 'gift_recipient_email_invalid' }),
    ...(!isSafeMessage(personalMessage ?? '', GIFT_CARD_MESSAGE_MAX_LENGTH) && {
      personalMessage: 'gift_message_invalid',
    }),
    ...(deliverAtOutOfRange && { deliverAt: 'gift_card_deliver_at_out_of_range' }),
  };
}

/** the days a shopper can pick; the last one is a day short of web's window, since its delivery hour may pass it */
export function giftCardDeliveryDays(now = DateTime.local()) {
  return { min: now.toISODate(), max: now.plus({ days: GIFT_CARD_DELIVERY_WINDOW_DAYS - 1 }).toISODate() };
}

/** today sends at purchase; a later day sends in the late morning */
export function giftCardDeliverAt(day: string, now = DateTime.local()): string | null {
  return day === now.toISODate() ? null : DateTime.fromISO(day).set({ hour: GIFT_CARD_DELIVERY_HOUR }).toISO();
}

export function giftCardDeliveryDay(deliverAt: string | null | undefined, now = DateTime.local()): string {
  return deliverAt ? DateTime.fromISO(deliverAt).toISODate() : now.toISODate();
}

export function billsBuyerOnToken(gift: { shipsTo: GiftShipsTo } | undefined): boolean {
  return gift?.shipsTo === GiftShipsTo.RECIPIENT;
}

export function giftOptionsValid(value: GiftOptionsValue): boolean {
  return Object.keys(giftOptionsErrors(value)).length === 0;
}

/** shipped to the buyer, there is no one to email, so the email and message stay off the input */
export function toGiftRecipientInput({ shipsTo, email, message }: GiftOptionsValue): GiftRecipientInput {
  if (shipsTo === GiftShipsTo.PURCHASER) {
    return { shipsTo };
  }
  const trimmedMessage = message.trim();
  return { shipsTo, email: email.trim(), ...(trimmedMessage && { message: trimmedMessage }) };
}
